import React, { useEffect, useState } from "react";
import "./DashboardPage.css";
import { DashboardSidebar } from "./Header";
import { getFirestore, collection, onSnapshot, doc, getDoc, updateDoc, deleteDoc } from "firebase/firestore";
import { updateProfile } from "firebase/auth";
import { app } from "../firebase";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import Skeleton from "@mui/material/Skeleton";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { useNavigate } from "react-router-dom";
import { auth } from "../firebase";

function DashboardPage({ photoURL, value }) {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [responseCounts, setResponseCounts] = useState({}); // { [formId]: count }
  const [accountAnchorEl, setAccountAnchorEl] = useState(null);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, formName } | null
  const [deleting, setDeleting] = useState(false);
  const [showAllForms, setShowAllForms] = useState(false);
  const navigate = useNavigate();
  const user = auth.currentUser;

  useEffect(() => {
    if (!user) return;
    setDisplayName(user?.displayName || "");
    const ref = doc(getFirestore(app), "userprofilemaster", user.uid);
    getDoc(ref).then((snap) => {
      if (snap.exists() && snap.data().displayName) {
        setDisplayName(snap.data().displayName);
      }
    });
  }, [user]);

  const handleProfileOpen = () => {
    setProfileDialogOpen(true);
    setAccountAnchorEl(null);
  };

  const handleProfileClose = () => {
    setProfileDialogOpen(false);
  };

  const handleProfileSave = async () => {
    if (!user) return;
    setSavingProfile(true);
    try {
      await updateProfile(user, { displayName });
      const ref = doc(getFirestore(app), "userprofilemaster", user.uid);
      await updateDoc(ref, { displayName });
      setSnack({ open: true, msg: "Profile updated successfully!", severity: "success" });
      handleProfileClose();
    } catch (err) {
      setSnack({ open: true, msg: "Failed to save changes.", severity: "error" });
    }
    setSavingProfile(false);
  };

  const logout = async () => {
    try {
      await auth.signOut();
      sessionStorage.clear();
      setAccountAnchorEl(null);
      navigate("/signin", { replace: true });
    } catch (error) {
      console.error("Unable to sign out:", error);
    }
  };

  useEffect(() => {
    const formRef = collection(getFirestore(app), "forms");
    const unsubscribe = onSnapshot(formRef, (snapshot) => {
      const userEmail = sessionStorage.getItem("email");
      setForms(snapshot.docs
        .map((form) => ({ id: form.id, ...form.data() }))
        .filter((form) => form.saveEmail === userEmail));
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  // Response counts live under a separate top-level "responses" collection:
  // responses/{formId}/responses/{responseId} — not on the form doc itself.
  // Subscribe to each form's response subcollection to get a live count.
  const formIdsKey = forms.map((form) => form.id).sort().join(",");

  useEffect(() => {
    if (!formIdsKey) {
      setResponseCounts({});
      return;
    }

    const db = getFirestore(app);
    const ids = formIdsKey.split(",").filter(Boolean);

    const unsubscribers = ids.map((formId) => {
      const responsesRef = collection(db, "responses", formId, "responses");
      return onSnapshot(
        responsesRef,
        (snapshot) => {
          setResponseCounts((prev) => ({ ...prev, [formId]: snapshot.size }));
        },
        (error) => {
          console.error(`Error listening to responses for form ${formId}:`, error);
        }
      );
    });

    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [formIdsKey]);

  const handleDeleteRequest = (form) => {
    setDeleteTarget({ id: form.id, formName: form.formName || "Untitled Form" });
  };

  const handleDeleteCancel = () => {
    if (deleting) return;
    setDeleteTarget(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteDoc(doc(getFirestore(app), "forms", deleteTarget.id));
      setSnack({ open: true, msg: "Form deleted successfully.", severity: "success" });
      setDeleteTarget(null);
    } catch (error) {
      console.error("Unable to delete form:", error);
      setSnack({ open: true, msg: "Failed to delete the form. Please try again.", severity: "error" });
    }
    setDeleting(false);
  };

  const totalResponses = forms.reduce(
    (total, form) => total + (responseCounts[form.id] || 0),
    0
  );
  const activeForms = forms.filter(
    (form) => (responseCounts[form.id] || 0) > 0
  ).length;
  const stats = [
    { label: "Total Forms", value: forms.length, icon: <DescriptionOutlinedIcon />, color: "#1767e8", background: "#e8f0fe" },
    { label: "Total Responses", value: totalResponses, icon: <AssignmentOutlinedIcon />, color: "#2e7d32", background: "#e8f5e9" },
    { label: "Active Forms", value: activeForms, icon: <CheckCircleOutlineRoundedIcon />, color: "#7e57c2", background: "#ede7f6" },
    { label: "Avg Responses / Form", value: forms.length ? (totalResponses / forms.length).toFixed(1) : "—", icon: <InsightsOutlinedIcon />, color: "#e65100", background: "#fff3e0" },
  ];
  const sortedForms = [...forms].sort((first, second) => {
    const firstCount = responseCounts[first.id] || 0;
    const secondCount = responseCounts[second.id] || 0;
    return secondCount - firstCount;
  });
  const visibleForms = showAllForms ? sortedForms : sortedForms.slice(0, 3);

  return (
    <div className="dashboard-page">
      <DashboardSidebar activePage="Dashboard" />
      <main className="dashboard-main">
        <header className="dashboard-summary-header">
          <div><h1>Dashboard</h1><p>Your forms, response health, and quick actions in one place.</p></div>
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => navigate("/create-form")} sx={{ backgroundColor: "#673ab7", textTransform: "none", fontWeight: 700, borderRadius: 2, "&:hover": { backgroundColor: "#512da8" } }}>Create form</Button>
          <Tooltip title="Open account menu"><IconButton className="dashboard-user-button" onClick={(event) => setAccountAnchorEl(event.currentTarget)} aria-label="Open account menu"><Avatar className="dashboard-user-avatar" src={photoURL || undefined} alt={value || "Account"}>{value ? value.charAt(0).toUpperCase() : "U"}</Avatar></IconButton></Tooltip>
        </header>
        <Menu anchorEl={accountAnchorEl} open={Boolean(accountAnchorEl)} onClose={() => setAccountAnchorEl(null)}>
          <MenuItem onClick={handleProfileOpen}>Profile Settings</MenuItem>
          <MenuItem onClick={logout}>Logout</MenuItem>
        </Menu>

        <section className="dashboard-stat-grid" aria-label="Form summary">
          {loading ? [...Array(4)].map((_, index) => <Skeleton key={index} variant="rounded" height={100} />) : stats.map((stat) => (
            <article className="dashboard-stat-card" key={stat.label}>
              <span className="dashboard-stat-icon" style={{ color: stat.color, background: stat.background }}>{stat.icon}</span>
              <div><strong>{stat.value}</strong><span>{stat.label}</span></div>
            </article>
          ))}
        </section>

        <section className="dashboard-forms-panel">
          <div className="dashboard-panel-heading"><div><h2>Recent forms</h2><p>Open a form to edit it or review its responses.</p></div>{forms.length > 3 && <Button onClick={() => setShowAllForms((current) => !current)} endIcon={<ArrowForwardRoundedIcon />} sx={{ textTransform: "none", color: "#673ab7", fontWeight: 700 }}>{showAllForms ? "Show recent" : "View all forms"}</Button>}</div>
          {loading ? <Skeleton variant="rounded" height={200} /> : forms.length === 0 ? (
            <div className="dashboard-empty"><InsightsOutlinedIcon /><p>No data yet. Create a form and collect responses to see your dashboard.</p></div>
          ) : (
            <div className="dashboard-table-scroll"><table className="dashboard-table">
              <thead><tr><th>#</th><th>Form Name</th><th>Responses</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>{visibleForms.map((form, index) => {
                const responses = responseCounts[form.id] || 0;
                return (
                  <tr key={form.id}>
                    <td>{index + 1}</td>
                    <td><button className="dashboard-form-link" onClick={() => navigate(`/form/${form.id}`)}>{form.formName || "Untitled Form"}</button></td>
                    <td><span className="dashboard-count">{responses}</span></td>
                    <td><span className={`dashboard-status ${responses ? "is-active" : "is-inactive"}`}>{responses ? "Active" : "No responses"}</span></td>
                    <td>
                      <Tooltip title="Edit form"><IconButton aria-label="Edit form" size="small" onClick={() => navigate(`/form/${form.id}`)} sx={{ color: "#673ab7" }}><EditOutlinedIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="View responses"><IconButton aria-label="View responses" size="small" onClick={() => navigate(`/form/${form.id}/responses`)} sx={{ color: "#1767e8" }}><AssignmentOutlinedIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete form">
                        <IconButton
                          aria-label="Delete form"
                          size="small"
                          onClick={() => handleDeleteRequest(form)}
                          sx={{
                            color: "#d93025",
                            "&:hover": { backgroundColor: "#fdeceb" },
                          }}
                        >
                          <DeleteOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </td>
                  </tr>
                );
              })}</tbody>
            </table></div>
          )}
        </section>

        <Dialog open={profileDialogOpen} onClose={handleProfileClose} maxWidth="sm" fullWidth>
          <DialogTitle>Profile Settings</DialogTitle>
          <DialogContent sx={{ py: 3 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Display Name"
              type="text"
              fullWidth
              variant="outlined"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleProfileClose}>Cancel</Button>
            <Button onClick={handleProfileSave} variant="contained" disabled={savingProfile}>
              {savingProfile ? "Saving…" : "Save"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={Boolean(deleteTarget)} onClose={handleDeleteCancel} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 600 }}>Delete this form?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {deleteTarget
                ? `"${deleteTarget.formName}" will be permanently deleted. Its responses will remain stored separately and can be removed manually if needed. This action cannot be undone.`
                : ""}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleDeleteCancel} disabled={deleting}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="contained"
              color="error"
              disabled={deleting}
            >
              {deleting ? "Deleting…" : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snack.open}
          autoHideDuration={3500}
          onClose={() => setSnack({ ...snack, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity={snack.severity} sx={{ width: "100%" }}>
            {snack.msg}
          </Alert>
        </Snackbar>
      </main>
    </div>
  );
}

export default DashboardPage;